import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { motion, useReducedMotion, type Variants } from 'framer-motion'

// ─── Data ────────────────────────────────────────────────────────────────────

const PAINS = [
  {
    icon: '📊',
    title: 'Excel pour tout',
    description: "Vos commissions TJM calculées à la main, vos splits partenaires sur un coin de fichier, vos factures dans Word. Vous passez 3h par semaine sur de l'administratif pur.",
  },
  {
    icon: '🔀',
    title: 'Outils éparpillés',
    description: 'Vivier dans un CRM, mandats dans un autre outil, factures dans un logiciel comptable, relances dans votre tête. Rien ne se parle.',
  },
  {
    icon: '🤝',
    title: 'Partenaires mal gérés',
    description: 'Partager un besoin avec un partenaire demande 5 emails. Suivre qui a envoyé quoi, calculer le split à la fin — une source permanente de friction.',
  },
]

const FEATURES: { icon: string; title: string; description: string; badge: 'exclusif' | 'ia' | null }[] = [
  {
    icon: '👥',
    title: 'Vivier candidats intelligent',
    description: "Freelances, CDI/CDD, portage salarial. Import LinkedIn en 1 clic via l'extension Chrome. Parsing CV automatique par IA. Recherche par compétences.",
    badge: null,
  },
  {
    icon: '📋',
    title: 'Mandats & Pipeline Kanban',
    description: 'Gérez vos besoins clients avec priorités, probabilité de signature, mode régie ou forfait. Pipeline visuel pour suivre chaque candidat.',
    badge: null,
  },
  {
    icon: '💰',
    title: 'Commissions TJM automatiques',
    description: 'Calcul automatique de vos commissions freelance selon TJM, taux et jours travaillés. Convertisseur portage salarial → TJM intégré.',
    badge: 'exclusif',
  },
  {
    icon: '🤝',
    title: 'Splits partenaire automatiques',
    description: "Gérez vos apporteurs d'affaires avec splits en % ou montant fixe. Invitez des partenaires externes à soumettre des profils via un lien unique.",
    badge: 'exclusif',
  },
  {
    icon: '📄',
    title: 'Facturation PDF intégrée',
    description: 'Générez et envoyez vos factures directement depuis RecrutPro. Envoi automatique en fin de mois, suivi des paiements, relances.',
    badge: null,
  },
  {
    icon: '🤖',
    title: 'Intelligence artificielle',
    description: "Parsing CV, transcription d'entretiens, recherche de candidats en langage naturel, résumés de profils. Powered by Claude AI.",
    badge: 'ia',
  },
  {
    icon: '⚡',
    title: 'Automatisations fin de mois',
    description: 'Email automatique aux freelances pour déclarer leurs jours. Envoi automatique des factures aux clients. Tout configurable dans vos paramètres.',
    badge: null,
  },
  {
    icon: '🔗',
    title: 'Lien client & partenaire',
    description: "Partagez un lien unique à votre client pour qu'il soumette ses besoins. Invitez vos partenaires à soumettre des candidats sans qu'ils aient de compte.",
    badge: 'exclusif',
  },
]

const COMPARISON: [string, boolean, boolean, boolean, boolean][] = [
  ['Vivier candidats',                  true, true,  true,  true],
  ['Pipeline Kanban',                   true, true,  true,  true],
  ['Commissions TJM auto',              true, false, false, false],
  ['Splits partenaire auto',            true, false, false, false],
  ['Facturation PDF intégrée',          true, false, true,  false],
  ['Lien partenaire externe',           true, false, false, false],
  ['IA native (Claude)',                true, false, false, false],
  ['Automatisations fin de mois',       true, false, false, false],
  ['Convertisseur portage → TJM',       true, false, false, false],
]

const PRICING_FEATURES = [
  'Vivier candidats illimité',
  'Mandats & pipeline kanban',
  'Commissions TJM automatiques',
  'Splits partenaire automatiques',
  'Facturation PDF intégrée',
  'Extension Chrome LinkedIn',
  'IA — parsing CV, transcription, assistant',
  'Lien client & partenaire externe',
  'Automatisations fin de mois',
  'Co-recrutement',
  'Support inclus',
]

// ─── Animation ────────────────────────────────────────────────────────────────

function useSectionVariants(): Variants {
  const prefersReduced = useReducedMotion()
  return {
    hidden:  { opacity: 0, y: prefersReduced ? 0 : 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Landing() {
  const prefersReduced = useReducedMotion()
  const sectionVariants = useSectionVariants()
  const [billing, setBilling] = useState<'month' | 'year'>('month')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const fadeUp = (delay = 0) => ({
    initial:    { opacity: 0, y: prefersReduced ? 0 : 20 },
    animate:    { opacity: 1, y: 0 },
    transition: { delay, duration: 0.5 },
  })

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white font-sans">

      {/* ── 1. Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="font-bold text-xl text-slate-900 dark:text-white">
            Recrut<span className="text-indigo-600 dark:text-indigo-400">Pro</span>
          </span>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-600 dark:text-slate-400">
            <a href="#features" className="hover:text-slate-900 dark:hover:text-white transition-colors">Fonctionnalités</a>
            <a href="#pricing" className="hover:text-slate-900 dark:hover:text-white transition-colors">Tarifs</a>
            <Link to="/login" className="hover:text-slate-900 dark:hover:text-white transition-colors">Se connecter</Link>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/register"
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
              Essai gratuit 14j →
            </Link>
            <button
              className="md:hidden p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-slate-900 border-t border-slate-200/50 dark:border-slate-800/50 px-4 py-3 flex flex-col gap-3 text-sm text-slate-600 dark:text-slate-400">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="py-1 hover:text-slate-900 dark:hover:text-white transition-colors">Fonctionnalités</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="py-1 hover:text-slate-900 dark:hover:text-white transition-colors">Tarifs</a>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="py-1 hover:text-slate-900 dark:hover:text-white transition-colors">Se connecter</Link>
          </div>
        )}
      </nav>

      {/* ── 2. Hero ── */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-gradient-to-br from-indigo-100 dark:from-indigo-900/30 via-indigo-50 dark:via-indigo-900/10 to-transparent blur-3xl pointer-events-none"
        />
        <div className="relative max-w-4xl mx-auto text-center">

          <motion.div {...fadeUp(0)}>
            <span className="inline-flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-medium px-3 py-1.5 rounded-full border border-indigo-200 dark:border-indigo-800 mb-6">
              ✨ Conçu par des recruteurs, pour des recruteurs
            </span>
          </motion.div>

          <motion.h1
            {...fadeUp(0.1)}
            className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6 leading-tight tracking-tight"
          >
            L'ATS qui comprend
            <br />
            <span className="text-indigo-600 dark:text-indigo-400">votre métier</span>
          </motion.h1>

          <motion.p
            {...fadeUp(0.2)}
            className="text-lg md:text-xl text-slate-600 dark:text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            RecrutPro centralise vos mandats, calcule vos commissions TJM automatiquement,
            génère vos factures et synchronise avec vos partenaires.
            Tout ce qu'aucun autre ATS ne fait pour les recruteurs indépendants.
          </motion.p>

          <motion.div
            {...fadeUp(0.3)}
            className="flex flex-col sm:flex-row gap-3 justify-center"
          >
            <Link to="/register"
              className="bg-indigo-600 text-white px-8 py-3.5 rounded-xl text-base font-medium hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-600/20">
              Démarrer gratuitement — 14 jours →
            </Link>
            <a href="#features"
              className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-8 py-3.5 rounded-xl text-base font-medium border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
              Voir les fonctionnalités
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-sm text-slate-400 dark:text-slate-500 mt-6"
          >
            Aucune carte requise · 14 jours gratuits · Annulation à tout moment
          </motion.p>

        </div>
      </section>

      {/* ── 3. Douleurs ── */}
      <section className="py-20 px-4 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-5xl mx-auto">

          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">
              Vous reconnaissez-vous ?
            </h2>
            <p className="text-slate-500 dark:text-slate-400">
              Le quotidien d'un recruteur indépendant sans le bon outil.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {PAINS.map((pain, i) => (
              <motion.div
                key={pain.title}
                variants={sectionVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: prefersReduced ? 0 : i * 0.1 }}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700"
              >
                <div className="text-3xl mb-4">{pain.icon}</div>
                <p className="text-xs font-medium text-red-500 dark:text-red-400 mb-2">Avant RecrutPro</p>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{pain.title}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{pain.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. Features ── */}
      <section id="features" className="py-20 px-4 scroll-mt-20">
        <div className="max-w-5xl mx-auto">

          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
              RecrutPro couvre tout le cycle du recruteur indépendant —
              du premier contact candidat à la facture client payée.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={feature.title}
                variants={sectionVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: prefersReduced ? 0 : (i % 2) * 0.1 }}
                className="flex gap-4 bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors"
              >
                <div className="text-2xl flex-shrink-0 mt-0.5">{feature.icon}</div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{feature.title}</h3>
                    {feature.badge && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        feature.badge === 'ia'
                          ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                          : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      }`}>
                        {feature.badge === 'ia' ? 'IA' : '⭐ Exclusif'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Différenciation ── */}
      <section className="py-20 px-4 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-4xl mx-auto">

          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">
              Pourquoi RecrutPro ?
            </h2>
            <p className="text-slate-500 dark:text-slate-400">
              Les autres ATS ont été conçus pour les RH d'entreprise.
              RecrutPro a été conçu pour vous.
            </p>
          </motion.div>

          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            className="overflow-x-auto"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-500 dark:text-slate-400 font-medium">Fonctionnalité</th>
                  <th className="py-3 px-4 text-center">
                    <span className="font-bold text-indigo-600 dark:text-indigo-400">RecrutPro</span>
                    <p className="text-xs text-indigo-500 dark:text-indigo-400 font-normal">39€/mois</p>
                  </th>
                  <th className="py-3 px-4 text-center text-slate-400 dark:text-slate-500 font-normal">
                    Huntool<br /><span className="text-xs">69€/mois</span>
                  </th>
                  <th className="py-3 px-4 text-center text-slate-400 dark:text-slate-500 font-normal">
                    Nicoka<br /><span className="text-xs">130€/mois</span>
                  </th>
                  <th className="py-3 px-4 text-center text-slate-400 dark:text-slate-500 font-normal">
                    Jarvi<br /><span className="text-xs">150€/mois</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(([label, rp, huntool, nicoka, jarvi]) => (
                  <tr key={label as string} className="border-b border-slate-100 dark:border-slate-800 hover:bg-white dark:hover:bg-slate-800/50">
                    <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{label}</td>
                    <td className="py-3 px-4 text-center">
                      {rp ? <span className="text-emerald-500 font-bold">✓</span> : <span className="text-slate-300 dark:text-slate-600">—</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {huntool ? <span className="text-emerald-500">✓</span> : <span className="text-slate-300 dark:text-slate-600">—</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {nicoka ? <span className="text-emerald-500">✓</span> : <span className="text-slate-300 dark:text-slate-600">—</span>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {jarvi ? <span className="text-emerald-500">✓</span> : <span className="text-slate-300 dark:text-slate-600">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── 6. Pricing ── */}
      <section id="pricing" className="py-20 px-4 scroll-mt-20">
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="max-w-lg mx-auto text-center"
        >
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">
            Un seul plan. Tout inclus.
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-8">
            Pas de fonctionnalités cachées derrière un plan supérieur.
            Tout RecrutPro pour un prix unique.
          </p>

          {/* Toggle mensuel/annuel */}
          <div className="inline-flex items-center bg-slate-100 dark:bg-slate-800 rounded-xl p-1 gap-1 mb-8">
            <button
              onClick={() => setBilling('month')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                billing === 'month'
                  ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-200'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBilling('year')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                billing === 'year'
                  ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-200'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              Annuel
              <span className="ml-1.5 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded-full">−20%</span>
            </button>
          </div>

          {/* Carte prix */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 border-2 border-indigo-200 dark:border-indigo-800 shadow-xl shadow-indigo-600/10">

            <div className="mb-6">
              <div className="flex items-end justify-center gap-1 mb-1">
                <span className="text-6xl font-bold text-slate-900 dark:text-white">
                  {billing === 'month' ? '39' : '31'}
                </span>
                <span className="text-slate-400 dark:text-slate-500 mb-3">€/mois</span>
              </div>
              {billing === 'year' ? (
                <p className="text-sm text-emerald-600 dark:text-emerald-400">Facturé 372€/an — économisez 96€</p>
              ) : (
                <p className="text-sm text-slate-400 dark:text-slate-500">ou 31€/mois en annuel</p>
              )}
            </div>

            <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400 mb-8 text-left">
              {PRICING_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <span className="text-emerald-500 flex-shrink-0">✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Link to="/register"
              className="block w-full bg-indigo-600 text-white py-4 rounded-xl font-medium text-base hover:bg-indigo-700 transition-colors text-center shadow-lg shadow-indigo-600/20">
              Démarrer 14 jours gratuits →
            </Link>

            <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 text-center">
              Aucune carte requise · 14 jours gratuits · Annulation à tout moment
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── 7. CTA final ── */}
      <motion.section
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        className="py-20 px-4 bg-indigo-600"
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Prêt à reprendre le contrôle ?
          </h2>
          <p className="text-indigo-200 mb-8 text-lg">
            14 jours gratuits. Aucune carte débitée. Toutes les fonctionnalités incluses.
          </p>
          <Link to="/register"
            className="inline-block bg-white text-indigo-600 px-10 py-4 rounded-xl font-semibold text-base hover:bg-indigo-50 transition-colors shadow-xl">
            Commencer gratuitement →
          </Link>
        </div>
      </motion.section>

      {/* ── 8. Footer ── */}
      <footer className="py-12 px-4 bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-8">

            <div>
              <p className="font-bold text-white text-lg mb-2">
                Recrut<span className="text-indigo-400">Pro</span>
              </p>
              <p className="text-slate-400 text-sm max-w-xs">
                L'ATS conçu par des recruteurs, pour des recruteurs indépendants et cabinets de recrutement.
              </p>
            </div>

            <div className="flex gap-12">
              <div>
                <p className="text-slate-300 font-medium text-sm mb-3">Produit</p>
                <div className="space-y-2 text-slate-400 text-sm">
                  <a href="#features" className="block hover:text-white transition-colors">Fonctionnalités</a>
                  <a href="#pricing" className="block hover:text-white transition-colors">Tarifs</a>
                  <Link to="/register" className="block hover:text-white transition-colors">Essai gratuit</Link>
                </div>
              </div>
              <div>
                <p className="text-slate-300 font-medium text-sm mb-3">Légal</p>
                <div className="space-y-2 text-slate-400 text-sm">
                  <Link to="/legal/cgu" className="block hover:text-white transition-colors">CGU</Link>
                  <Link to="/legal/confidentialite" className="block hover:text-white transition-colors">Confidentialité</Link>
                  <Link to="/legal/mentions" className="block hover:text-white transition-colors">Mentions légales</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-2">
            <p className="text-slate-500 text-xs">
              © {new Date().getFullYear()} RecrutPro — Bismuth Dany · SIREN 100 821 529
            </p>
            <p className="text-slate-500 text-xs">
              Fait avec ❤️ pour les recruteurs indépendants
            </p>
          </div>
        </div>
      </footer>

    </div>
  )
}
